import React, { useEffect, useState, useRef } from "react";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useLoadScript
} from "@react-google-maps/api";
import { BASE_URL } from "../../../components/BaseUrl";
import { decryptToken } from "../../../Utlis/Encryption";
import CompanyMarkersTable from "./PinsTable";
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Switch,
  CircularProgress,
  IconButton,
  FormControl
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { Country, State, City } from "country-state-city";
import { useTranslation } from "react-i18next";
import SearchableDropdown from "../../../components/SearchComponent";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CustomButton from "../../../components/CustomButton";
import { toast } from "react-hot-toast"
import { MAPS_API_KEY } from "../../../components/mapKey";



const DashboardPins = () => {

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: MAPS_API_KEY,
  });

  const rawEncryptedToken = localStorage.getItem("token");
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

  const { t } = useTranslation();
  const mapRef = useRef(null);

  // =====================================================
  // STATES
  // =====================================================
  const [filterOpen, setFilterOpen] = useState(false);
  const [combined, setCombined] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const [active, setActive] = useState(false);
  const [inactive, setInactive] = useState(false);
  const [invited, setInvited] = useState(false);
  const [requested, setRequested] = useState(false);
  const [trial, setTrial] = useState(false);

  const [paymentOverdue, setPaymentOverdue] = useState(false);
  const [free, setFree] = useState(false);
  const [plan, setPlan] = useState(false);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const hasFitted = useRef(false);
  const [zoneCountries, setZoneCountries] = useState([]);
  const [zoneStates, setZoneStates] = useState([]);
  const [zoneCities, setZoneCities] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCountryIso, setSelectedCountryIso] = useState("");

  const [selectedState, setSelectedState] = useState("");
  const [selectedStateIso, setSelectedStateIso] = useState("");

  const [selectedCity, setSelectedCity] = useState("");

  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const [limit, setLimit] = useState(5);
  const [applying, setApplying] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [filterParams, setFilterParams] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  // =====================================================
  // LOAD DEFAULT MAP DATA
  useEffect(() => {
    if (isFilterApplied && filterParams) {
      fetchFilteredData(filterParams, page);
    } else {
      loadDefault();
    }
  }, [page, isFilterApplied, filterParams]);

  const loadDefault = async () => {
    setLoading(true);

    const params = new URLSearchParams({
      limit,
      page: page + 1, // API uses 1-based pages
    });

    const res = await fetch(`${BASE_URL}/maps/my-companies/all?${params.toString()}`, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    });

    const json = await res.json();
    console.log(
      "📍 DashboardPins.js → /maps/my-companies response marker sample:",
      json.data?.markers?.[0]
    );


    console.log(
      "📍 DashboardPins.js → marker keys:",
      Object.keys(json.data?.markers?.[0] || {})
    );
    if (!json.error) {
      //    const transformed = json.data.markers.map((item) => ({
      //   id: item.id,
      //   title: item.title,
      //   status: item.status, 
      //   company_status:item.company_status, // ✅ ADD THIS  // your API has "status"
      //   latitude: item.position?.lat ?? null,
      //   longitude: item.position?.lng ?? null,
      //   region: item.region,
      //   infoPreview: item.infoPreview,
      //   created_at: item.created_at
      // }));

      const transformed = json.data.markers.map((item) => ({
        id: item.id,
        title: item.title,
        status: item.status, // trial / paid
        company_status: item.company_status, // invited / active / requested
        latitude: item.position?.lat ?? null,
        longitude: item.position?.lng ?? null,
        region: item.region,
        info: item.info, // ✅ PASS FULL INFO OBJECT
        created_at: item.created_at
      }));
      console.log("TRANSFORMED DEFAULT ROW:", transformed[0]);

      setCombined(transformed);
      setTotalCount(json.data?.pagination?.total || 0);

      // total pages = ceil(total / limit)
      const tp = Math.ceil((json.data?.pagination?.total || 1) / limit);
      setTotalPages(tp);
    }

    setLoading(false);
  };


  // =====================================================
  // LOCATION LISTS
  // =====================================================
  const fetchCountriesAndStates = () => {
    setLoadingCountries(true);
    try {
      const all = Country.getAllCountries();
      setZoneCountries(all.map((c) => ({ name: c.name, isoCode: c.isoCode })));
    } finally {
      setLoadingCountries(false);
    }
  };

  const loadStates = (countryIso) => {
    setLoadingStates(true);
    try {
      const states = State.getStatesOfCountry(countryIso);
      setZoneStates(states.map((s) => ({ name: s.name, isoCode: s.isoCode })));
    } finally {
      setLoadingStates(false);
    }
  };

  const loadCities = (countryIso, stateIso) => {
    setLoadingCities(true);
    try {
      const cities = City.getCitiesOfState(countryIso, stateIso);
      setZoneCities(cities.map((c) => c.name));
    } finally {
      setLoadingCities(false);
    }
  };

  useEffect(() => {
    if (filterOpen) fetchCountriesAndStates();
  }, [filterOpen]);

  // =====================================================
  // HANDLERS FOR SEARCHABLE DROPDOWNS
  // =====================================================
  const handleCountryChange = (val) => {
    const countryName = val || "";
    setSelectedCountry(countryName);

    const found = zoneCountries.find((c) => c.name === countryName);
    const iso = found?.isoCode || "";
    setSelectedCountryIso(iso);

    // reset state + city
    setSelectedState("");
    setSelectedStateIso("");
    setSelectedCity("");
    setZoneStates([]);
    setZoneCities([]);

    if (iso) loadStates(iso);
  };

  const handleStateChange = (val) => {
    const stateName = val || "";
    setSelectedState(stateName);

    const found = zoneStates.find((s) => s.name === stateName);
    const iso = found?.isoCode || "";
    setSelectedStateIso(iso);

    setSelectedCity("");
    setZoneCities([]);

    if (selectedCountryIso && iso) {
      loadCities(selectedCountryIso, iso);
    }
  };

  const handleCityChange = (val) => {
    setSelectedCity(val || "");
  };

  // =====================================================
  // APPLY FILTERS
  // =====================================================
  const applyFilters = async () => {
    if (dateFrom && dateTo && new Date(dateTo) < new Date(dateFrom)) {
      toast.error(t("filters.errors.endDateBeforeStart"));
      return; // Stop filter apply
    }
    setApplying(true);
    // Always reset to first page before fetching
    const newPage = 0;
    setPage(newPage);
    setIsFilterApplied(true);
    const params = {
      active,
      inactive,
      invited,
      requested,
      trial,
      date_from: dateFrom,
      date_to: dateTo,
      country: selectedCountry,
      state: selectedState,
      city: selectedCity,
      limit,
    };
    setFilterParams(params);
    // fetchFilteredData will be called by useEffect
    setApplying(false);
    setFilterOpen(false);
  }
  const fetchFilteredData = async (params, page) => {
    setLoading(true);
    const urlParams = new URLSearchParams({
      ...params,
      page: page + 1,
    });
    try {
      const res = await fetch(`${BASE_URL}/maps/my-companies/all?${urlParams.toString()}`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });

      const json = await res.json();

      if (!json.error) {
        const filtered = json.data.markers.map((item) => ({
          // id: item.id,
          // title: item.title,
          // status: item.status,
          // company_status: item.company_status, // ✅ ADD THIS
          // latitude: item.position?.lat ?? null,
          // longitude: item.position?.lng ?? null,
          // region: item.region,
          // infoPreview: item.infoPreview,
          // created_at: item.created_at
          id: item.id,
          title: item.title,
          status: item.status, // trial / paid
          company_status: item.company_status, // invited / active / requested
          latitude: item.position?.lat ?? null,
          longitude: item.position?.lng ?? null,
          region: item.region,
          info: item.info, // ✅ PASS FULL INFO OBJECT
          created_at: item.created_at
        }));
        setCombined(filtered);
        setTotalCount(json.data?.pagination?.total || 0);
        const tp = Math.ceil((json.data?.pagination?.total || 1) / params.limit);
        setTotalPages(tp);
      }
    } catch (err) {
      toast.error(t("filters.errors.fetchFailed"));
    }
    setLoading(false);
  };


  // =====================================================
  // RESET FILTERS
  // =====================================================
  const resetFilters = () => {
    setActive(false);
    setInactive(false);
    setInvited(false);
    setRequested(false);
    setTrial(false);
    setPaymentOverdue(false);
    setFree(false);
    setPlan(false);

    setDateFrom("");
    setDateTo("");
    setSelectedCountry("");
    setSelectedCountryIso("");
    setSelectedState("");
    setSelectedStateIso("");
    setSelectedCity("");

    setZoneStates([]);
    setZoneCities([]);

    setLimit(5);
    setIsFilterApplied(false);
    setFilterParams(null);
    setPage(0);
    loadDefault();
  };
  const filterApplied =
    active ||
    inactive ||
    invited ||
    requested ||
    trial ||
    paymentOverdue ||
    free ||
    plan ||
    dateFrom ||
    dateTo ||
    selectedCountry ||
    selectedState ||
    selectedCity ||
    limit !== 5;
  const statusLegend = [
    { key: "active", label: t("active"), color: "#28a745" },        // green
    { key: "inactive", label: t("inactive"), color: "#F44336" },    // gray
    { key: "invited", label: t("invited"), color: "#17a2b8" },      // cyan
    { key: "requested", label: t("requested"), color: "#6610f2" },  // purple
    // { key: "trial", label: t("trial"), color: "#ffc107" },          // yellow
  ];

  useEffect(() => {
    if (!isLoaded || !mapRef.current || combined.length === 0) return;

    if (hasFitted.current) return; // ✅ prevent re-run

    const bounds = new window.google.maps.LatLngBounds();

    combined.forEach((item) => {
      if (item.latitude && item.longitude) {
        bounds.extend({
          lat: item.latitude,
          lng: item.longitude,
        });
      }
    });
    if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds);
    }

    hasFitted.current = true;
  }, [combined, isLoaded]);







  const getMarkerIcon = (status) => {
    switch (status) {
      case "active":
        return "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png";
      case "inactive":
        return "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png";
      case "invited":
        return "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png";
      case "requested":
        return "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-violet.png";
      case "trial":
        return "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-gold.png";
      default:
        return "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png";
    }
  };


  // =====================================================
  // SHOW LOADING
  // =====================================================
  if (loading) {
    return (
      <Box sx={{ height: "70vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  // =====================================================
  // UI
  // =====================================================
  return (
    <Box sx={{ p: 1, bgcolor: "#fff", border: "1px solid #E6E9EF", borderRadius: 1.5 }}>
      {/* HEADER */}
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", mb: 2 }}>
        <Typography sx={{
          fontFamily: 'Poppins, sans-serif',
          fontSize: "20px",
          color: "#252430",
          lineHeight: "26px",

          fontWeight: 500
        }}>
          {t("myCompanies")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 3,
            mb: 2,
            flexWrap: "wrap"
          }}
        >
          {statusLegend.map((item) => (
            <Box key={item.key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: item.color
                }}
              />
              <Typography sx={{ fontSize: 14, fontFamily: "Poppins, sans-serif" }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          {/* MAIN FILTER BUTTON */}
          <IconButton
            onClick={() => setFilterOpen(true)}
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1,
              border: filterApplied ? "1px solid #1976D2" : "1px solid #C4C4C4",
              bgcolor: filterApplied ? "#1976D2" : "white",
              color: filterApplied ? "#fff" : "#5E5C5C",
              transition: "all 0.2s ease",
            }}
          >
            <FilterAltIcon sx={{ fontSize: 20 }} />
          </IconButton>

          {/* CLEAR FILTER (ONLY WHEN APPLIED) */}
          {filterApplied && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation(); // prevent opening modal
                resetFilters();      // your existing reset function
              }}
              sx={{
                position: "absolute",
                top: -6,
                right: -6,
                width: 18,
                height: 18,
                bgcolor: "#fff",
                border: "1px solid #C4C4C4",
                p: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": {
                  bgcolor: "#f2f2f2",
                },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 12, color: "#1976D2" }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* MAP */}
      <Box sx={{ height: "70vh", width: "100%", borderRadius: "8px", overflow: "hidden" }}>

        <Box sx={{ height: "70vh", width: "100%", borderRadius: "8px", overflow: "hidden" }}>

          {isLoaded && (
            <GoogleMap
              center={{ lat: 31.5, lng: 74.3 }}
              zoom={5}
              mapContainerStyle={{ height: "100%", width: "100%" }}
              onLoad={(map) => (mapRef.current = map)}
            >
              {combined.map((item) =>
                item.latitude && item.longitude ? (
                  <Marker
                    key={item.id}
                    position={{
                      lat: item.latitude,
                      lng: item.longitude,
                    }}
                    icon={{
                      url: getMarkerIcon(item.company_status),
                      scaledSize: new window.google.maps.Size(25, 41),
                      anchor: new window.google.maps.Point(12, 41),
                    }}
                    onClick={() => setSelectedMarker(item)}
                  />
                ) : null
              )}

              {selectedMarker && (
                <InfoWindow
                  position={{
                    lat: selectedMarker.latitude,
                    lng: selectedMarker.longitude,
                  }}
                  onCloseClick={() => setSelectedMarker(null)}
                >
                  <div style={{ fontFamily: "Poppins, sans-serif" }}>
                    <strong>{selectedMarker.title}</strong>
                    <br />
                    {t(selectedMarker.company_status)}
                    <br />
                    {selectedMarker.region?.city}
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          )}

        </Box>


      </Box>
      <p style={{ textAlign: "center", marginTop: "10px" }}>
        {t("onlyValidCoordinates")}
      </p>
      <Box>
        <CompanyMarkersTable
          data={combined}
          page={page}
          totalPages={totalPages}
          onPageChange={(val) => setPage(val)}
        />
      </Box>
      {/* FILTER MODAL – same style as your example, but with SearchableDropdowns */}
      <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: "center" }}>{t("filtersData")}</DialogTitle>

        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* SWITCHES */}
          {[
            { label: t("active"), state: active, setter: setActive },
            { label: t("inactive"), state: inactive, setter: setInactive },
            { label: t("invited"), state: invited, setter: setInvited },
            { label: t("requested"), state: requested, setter: setRequested },
            // { label: t("trial"), state: trial, setter: setTrial },

          ].map((item, i) => (
            <Box sx={{ display: "flex", justifyContent: "space-between" }} key={i}>
              <Typography sx={{ fontSize: "18px", fontFamily: 'Poppins, sans-serif', }}>{item.label}</Typography>
              <Switch checked={item.state} onChange={() => item.setter(!item.state)} />
            </Box>
          ))}

          {/* DATE PICKERS */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: "#2C384C",
                fontFamily: "Poppins, sans-serif",
              }}
            >
              {t("fromDate")}
            </Typography>

            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "6px",
                border: "1px solid #C4C4C4",
                fontSize: "14px",
                fontFamily: "Poppins, sans-serif",
                outline: "none",
              }}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: "#2C384C",
                fontFamily: "Poppins, sans-serif",
              }}
            >
              {t("toDate")}
            </Typography>

            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "6px",
                border: "1px solid #C4C4C4",
                fontSize: "14px",
                fontFamily: "Poppins, sans-serif",
                outline: "none",
              }}
            />
          </Box>


          {/* COUNTRY – SearchableDropdown */}
          <FormControl
            fullWidth
            size="small"

            sx={{
              "& .MuiOutlinedInput-root": {
                height: 38,
                borderRadius: "6px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
                "& fieldset": {
                  borderColor: "#091E4224",
                  borderWidth: "2px",
                },
                "&:hover fieldset": {
                  borderColor: selectedState ? "#1976d2" : "#ccc",
                },
                "&.Mui-focused fieldset": {
                  borderColor: selectedState ? "#1976d2" : "#ccc",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#f5f5f5",
                },
              },
              "& .MuiInputLabel-root": {
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
                color: "#6B778C",
                "&.Mui-focused": {
                  color: selectedState ? "#1976d2" : "#ccc",
                },
              },
            }}
          >
            <SearchableDropdown
              value={selectedCountry}
              onChange={handleCountryChange}
              options={zoneCountries.map((c) => c.name)}
              placeholder="country"
              disabled={loadingCountries}
            />
          </FormControl>
          {/* STATE – SearchableDropdown */}
          <FormControl
            fullWidth
            size="small"

            sx={{
              "& .MuiOutlinedInput-root": {
                height: 38,
                borderRadius: "6px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
                "& fieldset": {
                  borderColor: "#091E4224",
                  borderWidth: "2px",
                },
                "&:hover fieldset": {
                  borderColor: selectedState ? "#1976d2" : "#ccc",
                },
                "&.Mui-focused fieldset": {
                  borderColor: selectedState ? "#1976d2" : "#ccc",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#f5f5f5",
                },
              },
              "& .MuiInputLabel-root": {
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
                color: "#6B778C",
                "&.Mui-focused": {
                  color: selectedState ? "#1976d2" : "#ccc",
                },
              },
            }}
          >
            <SearchableDropdown
              value={selectedState}
              onChange={handleStateChange}
              options={zoneStates.map((s) => s.name)}
              placeholder="state"
              disabled={!selectedCountry || loadingStates}
            />
          </FormControl>

          {/* CITY – SearchableDropdown */}
          <FormControl
            fullWidth
            size="small"

            sx={{
              "& .MuiOutlinedInput-root": {
                height: 38,
                borderRadius: "6px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
                "& fieldset": {
                  borderColor: "#091E4224",
                  borderWidth: "2px",
                },
                "&:hover fieldset": {
                  borderColor: selectedState ? "#1976d2" : "#ccc",
                },
                "&.Mui-focused fieldset": {
                  borderColor: selectedState ? "#1976d2" : "#ccc",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#f5f5f5",
                },
              },
              "& .MuiInputLabel-root": {
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
                color: "#6B778C",
                "&.Mui-focused": {
                  color: selectedState ? "#1976d2" : "#ccc",
                },
              },
            }}
          >
            <SearchableDropdown
              value={selectedCity}
              onChange={handleCityChange}
              options={zoneCities}
              placeholder="city"
              disabled={!selectedState || loadingCities}
            />
          </FormControl>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: "#2C384C",
                fontFamily: "Poppins, sans-serif"
              }}
            >
              {t("limit")}
            </Typography>

            <input
              type="number"
              value={limit}
              min={1}
              max={50000}
              onChange={(e) => {
                const val = e.target.value;

                if (val === "") {
                  setLimit("");
                  return;
                }

                const num = Number(val);
                if (num >= 1 && num <= 50000) {
                  setLimit(num);
                }
              }}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "6px",
                border: "1px solid #C4C4C4",
                fontSize: "14px",
                fontFamily: "Poppins, sans-serif",
                outline: "none",
              }}
            />
          </Box>

        </DialogContent>

        <DialogActions>
          <Button onClick={() => setFilterOpen(false)} sx={{
            borderRadius: "4.34px",
            textTransform: "none",
            fontWeight: 400,
            boxShadow: "none",
            fontFamily: 'Poppins, sans-serif',
            textTransform: "capitalize"
          }}>{t("cancel")}</Button>

          <CustomButton
            onClick={applyFilters}
            disabled={applying}
            size="medium"
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {applying && (
                  <CircularProgress size={18} thickness={4} sx={{ color: "#ffffff" }} />
                )}

                {t("apply")}
              </Box>
            }
          />

        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardPins;
